import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  ErrorState,
  Skeleton,
} from '@/components/ui';
import { formatNumber } from '@/lib/utils';

import { useUsage } from './admin-api';
import { FEATURE_KEYS, type RangeDays } from './admin-schemas';
import { BarList, percent, TimeBars } from './charts';
import { featureLabel, formatDay, langLabel, screenLabel } from './labels';

/** מזהים טכניים (מסלול, קוד, פעולה) — תמיד משמאל לימין. */
function Code({ children }: { children: string }) {
  return (
    <code
      dir="ltr"
      className="rounded-(--radius-sm) bg-surface-sunken px-1.5 py-0.5 font-mono text-2xs text-fg"
    >
      {children}
    </code>
  );
}

export function UsageSection({ days }: { days: RangeDays }) {
  const { t } = useTranslation('admin');
  const q = useUsage(days, true);
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton className="h-96 w-full" />;
  const d = q.data;

  const used = new Map(d.adoption.features.map((f) => [f.feature, f.businesses]));
  const adoption = FEATURE_KEYS.map((k) => ({
    key: k,
    label: featureLabel(k),
    value: used.get(k) ?? 0,
  })).sort((a, b) => b.value - a.value);
  const active = d.adoption.activeBusinesses;
  const f = d.funnel;
  const funnel = [
    { key: 'signedUp', label: t('funnel.signedUp'), value: f.signedUp },
    { key: 'onboarded', label: t('funnel.onboarded'), value: f.onboarded },
    { key: 'firstWork', label: t('funnel.firstWork'), value: f.firstWork },
    { key: 'activeWeek2', label: t('funnel.activeWeek2'), value: f.activeWeek2 },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader tone="accent">
          <CardTitle>{t('usage.dailyActive')}</CardTitle>
          <CardDescription>{t('usage.dailyActiveHint')}</CardDescription>
        </CardHeader>
        <CardContent>
          <TimeBars
            label={t('usage.dailyActive')}
            points={d.dailyActive.map((p) => ({
              key: p.day,
              value: p.users,
              title: formatDay(p.day),
            }))}
            describe={(p) =>
              t('usage.dayValue', { date: formatDay(p.key), count: formatNumber(p.value) })
            }
          />
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader tone="success">
            <CardTitle>{t('usage.adoption')}</CardTitle>
            <CardDescription>{t('usage.adoptionHint', { count: active })}</CardDescription>
          </CardHeader>
          <CardContent>
            <BarList
              tone="success"
              max={active}
              items={adoption.map((a) => ({ ...a, suffix: percent(a.value, active) }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader tone="orange">
            <CardTitle>{t('usage.funnel')}</CardTitle>
            <CardDescription>{t('usage.funnelHint')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <BarList
              tone="orange"
              max={f.signedUp}
              items={funnel.map((s) => ({ ...s, suffix: percent(s.value, f.signedUp) }))}
            />
            <p className="text-2xs text-fg-subtle">
              {t('funnel.week2Note', { count: f.week2Eligible })}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="overflow-hidden">
          <CardHeader tone="info">
            <CardTitle>{t('usage.topRoutes')}</CardTitle>
            <CardDescription>{t('usage.topRoutesHint')}</CardDescription>
          </CardHeader>
          {d.topRoutes.length === 0 ? (
            <CardContent>
              <p className="text-center text-xs text-fg-muted">{t('usage.noData')}</p>
            </CardContent>
          ) : (
            <DataTable
              caption={t('usage.topRoutes')}
              minWidth="24rem"
              rows={d.topRoutes}
              rowKey={(r) => r.route}
              columns={[
                { key: 'route', header: t('columns.route'), cell: (r) => <Code>{r.route}</Code> },
                {
                  key: 'views',
                  header: t('columns.views'),
                  numeric: true,
                  cell: (r) => formatNumber(r.views),
                },
                {
                  key: 'users',
                  header: t('columns.users'),
                  numeric: true,
                  cell: (r) => formatNumber(r.users),
                },
                {
                  key: 'businesses',
                  header: t('columns.businesses'),
                  numeric: true,
                  cell: (r) => formatNumber(r.businesses),
                },
              ]}
            />
          )}
        </Card>

        <Card className="overflow-hidden">
          <CardHeader tone="violet">
            <CardTitle>{t('usage.topClicks')}</CardTitle>
            <CardDescription>{t('usage.topClicksHint')}</CardDescription>
          </CardHeader>
          {d.topClicks.length === 0 ? (
            <CardContent>
              <p className="text-center text-xs text-fg-muted">{t('usage.noData')}</p>
            </CardContent>
          ) : (
            <DataTable
              caption={t('usage.topClicks')}
              minWidth="24rem"
              rows={d.topClicks}
              rowKey={(r) => r.code}
              columns={[
                { key: 'code', header: t('columns.code'), cell: (r) => <Code>{r.code}</Code> },
                {
                  key: 'clicks',
                  header: t('columns.clicks'),
                  numeric: true,
                  cell: (r) => formatNumber(r.clicks),
                },
                {
                  key: 'users',
                  header: t('columns.users'),
                  numeric: true,
                  cell: (r) => formatNumber(r.users),
                },
                {
                  key: 'businesses',
                  header: t('columns.businesses'),
                  numeric: true,
                  cell: (r) => formatNumber(r.businesses),
                },
              ]}
            />
          )}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="overflow-hidden lg:col-span-2">
          <CardHeader tone="teal">
            <CardTitle>{t('usage.topActions')}</CardTitle>
          </CardHeader>
          {d.topActions.length === 0 ? (
            <CardContent>
              <p className="text-center text-xs text-fg-muted">{t('usage.noData')}</p>
            </CardContent>
          ) : (
            <DataTable
              caption={t('usage.topActions')}
              minWidth="24rem"
              rows={d.topActions}
              rowKey={(r) => r.action}
              columns={[
                {
                  key: 'action',
                  header: t('columns.action'),
                  cell: (r) => <Code>{r.action}</Code>,
                },
                {
                  key: 'count',
                  header: t('columns.count'),
                  numeric: true,
                  cell: (r) => formatNumber(r.count),
                },
                {
                  key: 'businesses',
                  header: t('columns.businesses'),
                  numeric: true,
                  cell: (r) => formatNumber(r.businesses),
                },
              ]}
            />
          )}
        </Card>

        <Card>
          <CardHeader tone="pink">
            <CardTitle>{t('usage.devices')}</CardTitle>
            <CardDescription>{t('usage.sessions')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <BarList
              tone="violet"
              items={d.sessionsByScreen.map((s) => ({
                key: s.screen,
                label: screenLabel(s.screen),
                value: s.sessions,
              }))}
            />
            <div>
              <p className="mb-2 text-xs font-semibold text-fg">{t('usage.languages')}</p>
              <BarList
                tone="teal"
                items={d.sessionsByLanguage.map((s) => ({
                  key: s.lang,
                  label: langLabel(s.lang),
                  value: s.sessions,
                }))}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
